<template>
  <div class="prediction-previews">
    <div v-if="sport === 'Football'" class="button-container">
      <div v-for="prediction in footballPredictions" :key="prediction.group" class="info-button">
        <div class="eyebrow">{{ prediction.group }}</div>
        <div class="odds">{{ formatOdds(prediction.decimalOdds) }}</div>
        <div class="category">{{ prediction.name }}</div>
        <div class="probability">{{ formatProbability(prediction.probability) }}</div>
      </div>
    </div>

    <div class="button-container">
      <div v-if="sport === 'Basketball' && basketballHighestPrediction" class="info-button">
        <div class="odds">{{ formatOdds(basketballHighestPrediction.decimalOdds) }}</div>
        <div class="category">{{ basketballHighestPrediction.name }}</div>
        <div class="probability">{{ formatProbability(basketballHighestPrediction.probability) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
const SPECIAL_GROUPS = [
  {
    group: 'Double Chance',
    markets: ['Home or Draw', 'Home or Away', 'Away or Draw'],
    minProbability: 0.6,
  },
  {
    group: 'Home Team Goals',
    markets: ['First Team Over 1.5', 'First Team Over 0.5'],
    minProbability: 0.6,
  },
  {
    group: 'Away Team Goals',
    markets: ['Second Team Over 1.5', 'Second Team Over 0.5'],
    minProbability: 0.6,
  },
  {
    group: 'Clean Sheet',
    markets: ['First Team Clean Sheet', 'Second Team Clean Sheet'],
    minProbability: 0.4,
  }
];

export default {
  props: {
    gameDetails: {
      type: Object,
      required: true
    },
    sport: {
      type: String,
      required: true
    }
  },
  computed: {
    classes() {
      return this.gameDetails?.classes || {};
    },
    footballPredictions() {
      const mainGroups = [
        this.pickBest('Result', ['Home Win', 'Draw', 'Away Win']),
        this.pickBest('Goals', ['Over 2.5', 'Under 2.5']),
        this.pickBest('BTTS', ['BTTS Yes', 'BTTS No']),
      ];

      const bestSpecial = SPECIAL_GROUPS
        .map((group) => this.pickBest(group.group, group.markets))
        .filter((pick, index) => pick.probability >= SPECIAL_GROUPS[index].minProbability)
        .sort((a, b) => b.probability - a.probability)[0];

      return bestSpecial ? [...mainGroups, bestSpecial] : mainGroups;
    },
    basketballHighestPrediction() {
      const results = [
        { name: 'Home Win', probability: this.classes['Home Win']?.probability ?? 0, decimalOdds: this.classes['Home Win']?.['decimal odds'] },
        { name: 'Draw', probability: this.classes['Draw']?.probability ?? 0, decimalOdds: this.classes['Draw']?.['decimal odds'] },
        { name: 'Away Win', probability: this.classes['Away Win']?.probability ?? 0, decimalOdds: this.classes['Away Win']?.['decimal odds'] }
      ];
      return results.reduce((a, b) => a.probability > b.probability ? a : b);
    }
  },
  methods: {
    pickBest(group, markets) {
      return markets
        .map((name) => ({
          group,
          name,
          probability: this.classes[name]?.probability ?? 0,
          decimalOdds: this.classes[name]?.['decimal odds']
        }))
        .reduce((a, b) => a.probability > b.probability ? a : b);
    },
    formatOdds(decimalOdds) {
      return decimalOdds !== undefined && decimalOdds !== null ? Number(decimalOdds).toFixed(2) : 'N/A';
    },
    formatProbability(probability) {
      return probability !== undefined ? `${(probability * 100).toFixed(1)}%` : 'N/A';
    }
  }
};
</script>

<style scoped>
.button-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  gap: 4%;
}

.info-button {
  width: min(120px, 28vw);
  min-height: 96px;
  background-color: #d4edda;
  border: 2px solid #c3e6cb;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px;
  box-sizing: border-box;
}

.eyebrow {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4c6a57;
  margin-bottom: 4px;
  text-align: center;
}

.odds,
.probability {
  font-size: x-small;
  color: #666;
}

.category {
  font-size: x-small;
  font-weight: bold;
  color: #333;
  text-align: center;
}

@media (max-width: 700px) {
  .button-container {
    gap: 10px;
  }

  .info-button {
    width: calc(50% - 10px);
    min-width: 130px;
  }
}
</style>
